import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "@/components/ui/Button";
import toast from "react-hot-toast";
import NeedHelpModel from "../../pages/NeedHelpModel";
import UserIcon from "@/components/icons/UserIcon";

import {
  Globe,
  AlertTriangle,
  Settings,
  LayoutDashboard,
  Users,
  FileText,
  X,
  Tally4,
  ArrowRight,
  User,
  Menu,
  LogOut,
  ClipboardList,
} from "lucide-react";

const LAYOUT_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/dashboard$/,
  /^\/profile$/,
  /^\/settings$/,
  /^\/mine-types$/,
  /^\/mine-types\/[^/]+\/category\/view$/,
  /^\/mine-types\/[^/]+\/category\/edit$/,
  /^\/reports$/,
  /^\/reports\/create$/,
  /^\/reports\/edit\/[^/]+$/,
  /^\/reports\/edit-values\/[^/]+$/,
  /^\/reports\/view\/[^/]+$/,
  /^\/reports\/stack\/[^/]+$/,
  /^\/clients$/,
  /^\/clients\/create$/,
  /^\/clients\/[^/]+\/edit$/,
  /^\/users$/,
  /^\/users\/create$/,
  /^\/users\/[^/]+\/edit$/,
  /^\/site$/,
  /^\/site\/create$/,
  /^\/site\/edit\/[^/]+$/,
  /^\/field-engineer-reports$/,
  /^\/field-engineer-reports\/edit-values\/[^/]+$/,
  /^\/peer-reviewer-reports$/,
  /^\/site-reviewer-reports$/,
  /^\/director-reviewer-reports$/,
  /^\/not-found$/,
];

const isKnownLayoutPath = (pathname) =>
  LAYOUT_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));

const Layout = () => {
  const { user, site, logout, isAdmin, isSuperAdmin, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNeedHelpOpen, setIsNeedHelpOpen] = useState(false);

  // Check if user is admin or super admin
  const isAdminUser = isAdmin || isSuperAdmin;

  // Redirect logic for users with single role
  useEffect(() => {
    if (shouldRedirectToRoleDashboard()) {
      const availableRoles = getAvailableReviewerRoles();
      if (availableRoles.length === 1) {
        // User has exactly one role, redirect to their dashboard
        const singleRole = availableRoles[0];
        if (location.pathname === "/dashboard" || location.pathname === "/") {
          navigate(singleRole.href);
        }
      }
    }
  }, [user, isAdminUser, location.pathname, navigate]);

  useEffect(() => {
    if (!isSidebarOpen) {
      document.body.style.overflow = "";
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isSidebarOpen]);

  const handleLogout = () => {
    toast.success("Logged out successfully!");
    logout();
    // Small delay to ensure the toast message is visible before navigation
    setTimeout(() => {
      navigate("/login");
    }, 500);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const renderMenuToggle = () => {
    if (!showSidebar) return null;
    return (
      <button
        onClick={toggleSidebar}
        className="p-2 text-gray-0 hover:text-red-500 bg-navy-100 rounded-full"
        aria-label="Toggle menu"
      >
        <Menu className="w-6 h-6" />
      </button>
    );
  };

  const [openSubmenu, setOpenSubmenu] = useState(null);

  const toggleSubmenu = (name) => {
    setOpenSubmenu((prev) => (prev === name ? null : name));
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some((item) => location.pathname.startsWith(item.href));
  };

  // Get available reviewer roles for the user (based ONLY on explicit user.roles)
  const getAvailableReviewerRoles = () => {
    const reviewerRoles = [
      {
        name: "Field Engineer",
        value: "field-engineer",
        href: "/field-engineer-reports",
        icon: ClipboardList,
      },
      {
        name: "Peer Reviewer",
        value: "peer-reviewer",
        href: "/peer-reviewer-reports",
        icon: ClipboardList,
      },
      {
        name: "Site Reviewer",
        value: "site-reviewer",
        href: "/site-reviewer-reports",
        icon: ClipboardList,
      },
      {
        name: "Director Reviewer",
        value: "director-reviewer",
        href: "/director-reviewer-reports",
        icon: ClipboardList,
      },
    ];

    const userRoles = (user?.roles || []).map((r) => r?.toString?.() || "").filter(Boolean);
    if (userRoles.length === 0) return [];

    const normalized = new Set(
      userRoles.flatMap((r) => {
        const lower = r.toLowerCase();
        return [lower, lower.replace(/\s+/g, "-")];
      })
    );

    return reviewerRoles.filter(
      (role) => normalized.has(role.value) || normalized.has(role.name.toLowerCase())
    );
  };

  // Check if user should be redirected to a specific role dashboard
  const shouldRedirectToRoleDashboard = () => {
    if (!user || isAdminUser) return false;
    const availableRoles = getAvailableReviewerRoles();
    return availableRoles.length === 1;
  };

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },

    // Show admin menu items ONLY for admin users
    ...(isAdminUser
      ? [
        { name: "Clients", href: "/clients", icon: Users },
        { name: "Sites", href: "/site", icon: Globe },
        // Report Maintenance logic - different for admin vs regular users
        ...(() => {
          const reviewerItems = getAvailableReviewerRoles();
          if (reviewerItems.length > 0) {
            return [
              {
                name: "Reports",
                icon: FileText,
                submenu: [
                  { name: "Admin", href: "/reports" },
                  ...reviewerItems.map((role) => ({
                    name: `Manage by ${role.name}`,
                    href: role.href,
                  })),
                ],
              },
            ];
          }
          return [{ name: "Reports", href: "/reports", icon: FileText }];
        })(),
        { name: "Users", href: "/users", icon: Users },
        { name: "Mine Types", href: "/mine-types", icon: AlertTriangle },
        { name: "Settings", href: "/settings", icon: AlertTriangle },
      ]
      :
      []
    ),
  ];

  const isActive = (href) => {
    if (href === "/dashboard") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(href);
  };

  const getPageTitle = () => {
    // Special handling for specific pages first
    if (location.pathname.startsWith("/field-engineer-reports")) return "Field Engineer Reports";
    if (location.pathname.startsWith("/peer-reviewer-reports")) return "Peer Reviewer Reports";
    if (location.pathname.startsWith("/site-reviewer-reports")) return "Site Reviewer Reports";
    if (location.pathname.startsWith("/director-reviewer-reports")) return "Director Reviewer Reports";
    if (location.pathname.startsWith("/clients")) return "Client Management";
    if (location.pathname.startsWith("/site")) return "Site Management";
    if (location.pathname.startsWith("/reports/stack")) return "Report Stack";
    if (location.pathname.startsWith("/reports")) return "Report Management";
    if (location.pathname.startsWith("/users")) return "User Management";
    if (location.pathname.startsWith("/mine-types")) return "Mine Type Management";
    if (location.pathname.startsWith("/settings")) return "Site Settings";
    if (location.pathname.startsWith("/profile")) return "Profile";

    // For dashboard, always return Overview
    if (location.pathname === "/dashboard" || location.pathname === "/") {
      return "Overview";
    }

    // Find active navigation item (including submenu items)
    let activeNav = navigation.find((item) => {
      if (item.href && isActive(item.href)) {
        return true;
      }
      if (item.submenu) {
        return item.submenu.some((subItem) => isActive(subItem.href));
      }
      return false;
    });

    // If we found a navigation item with submenu, check if it's a submenu item
    if (activeNav && activeNav.submenu) {
      const activeSubItem = activeNav.submenu.find((subItem) => isActive(subItem.href));
      if (activeSubItem) {
        // Return the submenu item name for specific routes
        if (activeSubItem.href === "/reports") return "Reports";
        if (activeSubItem.href === "/mine-types") return "Mine Types";
        if (activeSubItem.href === "/settings") return "Site Settings";
        // For reviewer routes, return the role-specific title
        if (activeSubItem.href.includes("reviewer")) {
          return activeSubItem.name;
        }
      }
    }

    const title = activeNav?.name || "Overview";
    return title;
  };

  // Determine if we should render the sidebar at all
  const showSidebar = isAdminUser;

  const isNotFoundPage =
    location.pathname === "/not-found" || !isKnownLayoutPath(location.pathname);

  return (
    <div className="">
      {/* Sidebar overlay */}
      {showSidebar && isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={closeSidebar}
        />
      )}

      <div className="container px-4 py-2 md:px-8 md:py-6">
        {/* Mobile Header */}
        <header className="mb-4 border-b border-gray-600 lg:hidden">
          <div className="flex items-center justify-between px-2 py-3 gap-2">
            {showSidebar && (
              renderMenuToggle()
            )}
            <Link to="/dashboard" className="flex items-center justify-center flex-1">
              <img src="/images/logo-white.svg" alt="Minesure Logo" className="h-8" />
            </Link>
            <div className="flex items-center space-x-2">
              <Link
                to="/profile"
                className="p-2 text-gray-0 hover:text-red-500 bg-navy-100 rounded-full"
                title="Profile"
              >
                <User className="w-5 h-5" />
              </Link>
              <Button
                onClick={handleLogout}
                variant="link"
                size="sm"
                className="p-2 text-gray-0 hover:text-red-500"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Desktop Header - Hidden below 1024px */}
        <header className="hidden border-t-4 border-b border-gray-600 lg:block">
          <div className="flex flex-wrap items-center justify-between py-4 md:flex-nowrap md:py-4">

            {/* Left side - Minesure Logo */}
            <div className="flex items-center">
              {showSidebar && <div className="mr-2">{renderMenuToggle()}</div>}
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center space-x-2 transition-opacity"
              >
                <img src="/images/logo-white.svg" alt="Minesure Logo" className="h-8" />
              </button>
            </div>

            {/* Middle - Page Title */}
            <div className="flex-1 text-center">
              <h1 className="text-2xl font-bold text-white uppercase select-none">
                {isNotFoundPage
                  ? "Page Not Found"
                  : location.pathname === "/dashboard" || location.pathname === "/"
                    ? "Overview"
                    : getPageTitle()}
              </h1>
            </div>
            {/* Right side - User actions */}
            <div className="flex items-center space-x-6">
              <Link
                onClick={() => setIsNeedHelpOpen(true)}
                className="flex items-center space-x-2 text-sm font-medium uppercase transition-colors text-gray-0 hover:text-red-600"
              >
                <span>Contact/Help</span>
              </Link>
              <Button
                onClick={handleLogout}
                variant="link"
                size="sm"
                className="flex items-center space-x-2 text-sm font-medium uppercase transition-colors text-gray-0 hover:no-underline hover:text-red-600"
              >
                Log Out
              </Button>
              {/* Profile Icon*/}
              <div className="flex items-center space-x-2">
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-sm font-medium transition-colors text-gray-0 hover:text-red-500"
                  title="email"
                >
                  <UserIcon className="w-5 h-5 mr-2 text-red-500" />
                  <span className="!m-0 text-sm">{user?.email}</span>
                </Link>

              </div>

            </div>
          </div>
          <NeedHelpModel
            isOpen={isNeedHelpOpen}
            onClose={() => setIsNeedHelpOpen(false)}
          />
        </header>

        <div className="flex flex-col pt-5 lg:flex-row lg:space-x-4">
          {/* Sidebar */}
          {showSidebar && (
            <div
              className={`fixed inset-y-0 left-0 z-50 flex w-80 transform flex-col overflow-hidden bg-navy-600 transition-transform duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
              {/* Sidebar header */}
              <div className="flex items-center justify-between flex-shrink-0 p-4 border-b border-gray-600">
                <Link to="/dashboard" className="flex items-center">
                  <img src="/images/logo-white.svg" alt="Minesure Logo" className="h-8" />
                </Link>
                <button
                  onClick={closeSidebar}
                  className="p-2 text-gray-0 hover:text-red-500 bg-navy-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Logo - Desktop only */}
              {/* Scrollable Sidebar Content */}
              <div className="flex-1 overflow-y-auto">
                {/* Sidebar Content */}
                {isAdminUser && (
                  <div className="m-2">
                    <div className="rounded-2xl bg-navy-100">
                      <div className="flex items-center justify-between px-4 py-6 border-b border-gray-600">
                        <div className="">
                          <h2 className="text-sm font-semibold uppercase text-gray-0">Menu</h2>
                        </div>
                      </div>
                      {/* Navigation */}
                      <nav className="flex flex-col pt-4">
                        {navigation.map((item, index) => {
                          const Icon = item.icon;
                          const hasSubmenu = item.submenu && item.submenu.length > 0;
                          const isSubmenuOpen = openSubmenu === item.name;
                          const isItemActive = item.href
                            ? isActive(item.href)
                            : hasSubmenu && isSubmenuActive(item.submenu);

                          return (
                            <div key={item.name}>
                              {hasSubmenu ? (
                                <button
                                  onClick={() => toggleSubmenu(item.name)}
                                  className={`inline-flex w-full items-center justify-between px-4 pb-4 text-sm font-semibold uppercase text-gray-0 ${isItemActive ? "text-red-500" : "hover:text-red-500"}`}
                                >
                                  <span className="flex items-center">
                                    {item.name}
                                    <ArrowRight
                                      className={`ml-1 h-3 w-3 font-semibold transition-transform ${isSubmenuOpen ? "rotate-90" : ""}`}
                                    />
                                  </span>
                                </button>
                              ) : (
                                <Link
                                  to={item.href}
                                  onClick={closeSidebar}
                                  className={`inline-flex items-center px-4 pb-4 text-sm font-semibold uppercase text-gray-0 last:border-b-0 ${index + 1} ${isActive(item.href) ? "text-red-500" : "hover:text-red-500"}`}
                                >
                                  {item.name}
                                  <ArrowRight className="w-3 h-3 ml-1 font-semibold" />
                                </Link>
                              )}

                              {hasSubmenu && (
                                <div
                                  className={`overflow-hidden rounded-b-lg bg-navy-100 transition-all duration-300 ease-in-out ${isSubmenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                                >
                                  <div
                                    className={`transform transition-transform duration-300 ease-in-out ${isSubmenuOpen ? "translate-y-0" : "-translate-y-2"}`}
                                  >
                                    {item.submenu.map((subItem) => (
                                      <Link
                                        key={subItem.name}
                                        to={subItem.href}
                                        onClick={closeSidebar}
                                        className={`block px-8 py-3 text-xs font-medium uppercase text-gray-0 transition-colors duration-200 last:border-b-0 ${location.pathname.startsWith(subItem.href) ? "text-red-500" : "hover:text-red-500"}`}
                                      >
                                        {subItem.name}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex w-full min-w-0 flex-col overflow-visible">
            {/* Page Content */}
            <main className="min-w-0">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </div >
  );
};

export default Layout;